import { lazy, Suspense, type ReactNode } from "react";

import { Skeleton } from "~/components/ui/skeleton";
import SignInContainer from "./containers/signin-container";

export interface RouteDefinition {
  /** Pattern with `:param` segments, matched left to right. */
  path: string;
  /** What the operator sees in the document title. */
  title: string;
  /**
   * Layout group. `"public"` is the one value that is not merely grouping: it
   * is the declaration that the route may be shown with no session.
   */
  area: "mail" | "mailboxes" | "domains" | "settings" | "public" | "legacy";
  wired: boolean;
  element: () => ReactNode;
}

const Loading = () => (
  <div className="space-y-3" aria-busy="true" aria-label="Loading">
    <Skeleton className="h-8 w-64" />
    <Skeleton className="h-4 w-96" />
    <Skeleton className="h-64 w-full" />
  </div>
);

const load = (factory: Parameters<typeof lazy>[0]) => {
  const Component = lazy(factory);
  return () => (
    <Suspense fallback={<Loading />}>
      <Component />
    </Suspense>
  );
};

const mail = load(() => import("./containers/mail-workspace-container"));

export const ROUTES: RouteDefinition[] = [
  {
    path: "/signin",
    title: "Sign in",
    area: "public",
    wired: true,
    // Eager: a 401 redirect must not land on the shared route Suspense
    // skeleton. That fallback is `aria-label="Loading"` and looks like a
    // gated page still hydrating (API keys was the report). `/signin` is
    // the only public document; it is not worth a separate chunk.
    element: () => <SignInContainer />,
  },
  { path: "/", title: "Inbox", area: "legacy", wired: true, element: load(() => import("./redirect")) },
  { path: "/mail", title: "Inbox", area: "mail", wired: true, element: mail },
  { path: "/mail/:mailboxId", title: "Inbox", area: "mail", wired: true, element: mail },
  { path: "/mail/:mailboxId/search", title: "Search", area: "mail", wired: true, element: mail },
  { path: "/mail/:mailboxId/:threadId", title: "Conversation", area: "mail", wired: true, element: mail },
  {
    path: "/mailboxes",
    title: "Mailboxes",
    area: "mailboxes",
    wired: true,
    element: load(() => import("./containers/mailboxes-list-container")),
  },
  {
    path: "/mailboxes/new",
    title: "Add a mailbox",
    area: "mailboxes",
    wired: true,
    element: load(() => import("./containers/mailboxes-new-container")),
  },
  {
    path: "/mailboxes/import",
    title: "Import mailboxes",
    area: "mailboxes",
    wired: true,
    element: load(() => import("./containers/mailboxes-import-container")),
  },
  { path: "/mailboxes/provision", title: "Add a mailbox", area: "legacy", wired: true, element: load(() => import("./redirect")) },
  {
    path: "/mailboxes/new/:jobId",
    title: "Provisioning",
    area: "mailboxes",
    wired: true,
    element: load(() => import("./containers/mailbox-provision-progress-container")),
  },
  { path: "/mailboxes/provision/:jobId", title: "Provisioning", area: "legacy", wired: true, element: load(() => import("./redirect")) },
  { path: "/mailboxes/:mailboxId/aliases", title: "Mailbox", area: "legacy", wired: true, element: load(() => import("./redirect")) },
  {
    path: "/mailboxes/:mailboxId",
    title: "Mailbox",
    area: "mailboxes",
    wired: true,
    element: load(() => import("./containers/mailbox-detail-container")),
  },
  {
    path: "/domains",
    title: "Domains",
    area: "domains",
    wired: true,
    element: load(() => import("./containers/domains-list-container")),
  },
  {
    path: "/domains/new",
    title: "Add a domain",
    area: "domains",
    wired: true,
    element: load(() => import("./containers/domain-new-container")),
  },
  {
    path: "/domains/import",
    title: "Import domains",
    area: "domains",
    wired: true,
    element: load(() => import("./containers/domains-import-container")),
  },
  { path: "/domains/provision", title: "Add a domain", area: "legacy", wired: true, element: load(() => import("./redirect")) },
  {
    path: "/domains/:domainId",
    title: "Domain",
    area: "domains",
    wired: true,
    element: load(() => import("./containers/domain-detail-container")),
  },
  {
    path: "/settings",
    title: "Settings",
    area: "settings",
    wired: true,
    element: load(() => import("./containers/workspace-settings-container")),
  },
  {
    path: "/settings/team",
    title: "Team",
    area: "settings",
    wired: true,
    element: load(() => import("./containers/team-container")),
  },
  {
    path: "/settings/api-keys",
    title: "API keys",
    area: "settings",
    wired: true,
    element: load(() => import("./containers/api-keys-container")),
  },
  {
    path: "/settings/security",
    title: "Security",
    area: "settings",
    wired: true,
    element: load(() => import("./containers/profile-security-container")),
  },
  {
    path: "/settings/operations",
    title: "Operations",
    area: "settings",
    wired: true,
    element: load(() => import("./containers/admin-queues-container")),
  },
  {
    path: "/help",
    title: "Help",
    area: "settings",
    wired: true,
    element: load(() => import("./containers/help-container")),
  },

  // Dead destinations kept as redirects so the edge still serves the bookmark.
  { path: "/settings/workspace", title: "Settings", area: "legacy", wired: true, element: load(() => import("./redirect")) },
  { path: "/settings/notifications", title: "Settings", area: "legacy", wired: true, element: load(() => import("./redirect")) },
  { path: "/profile", title: "Settings", area: "legacy", wired: true, element: load(() => import("./redirect")) },
  { path: "/profile/preferences", title: "Settings", area: "legacy", wired: true, element: load(() => import("./redirect")) },
  { path: "/profile/security", title: "Security", area: "legacy", wired: true, element: load(() => import("./redirect")) },
  { path: "/admin", title: "Operations", area: "legacy", wired: true, element: load(() => import("./redirect")) },
  { path: "/admin/queues", title: "Operations", area: "legacy", wired: true, element: load(() => import("./redirect")) },
  { path: "/admin/dead-letters", title: "Operations", area: "legacy", wired: true, element: load(() => import("./redirect")) },
  { path: "/admin/mailcow", title: "Operations", area: "legacy", wired: true, element: load(() => import("./redirect")) },
];

export const DELIBERATELY_UNROUTED = [] as const;

export const SIGN_IN_PATH = "/signin";

/**
 * Whether a route may be rendered to somebody who is not signed in.
 *
 * **Fails closed.** Everything is authenticated unless it says otherwise.
 */
export function isPublicRoute(route: RouteDefinition): boolean {
  return route.area === "public";
}

/** Split a concrete pathname against a `:param` pattern. */
export function matchRoute(pathname: string): { route: RouteDefinition; params: Record<string, string> } | undefined {
  const parts = pathname.replace(/\/+$/u, "").split("/").filter(Boolean);
  for (const route of ROUTES) {
    const pattern = route.path.split("/").filter(Boolean);
    if (pattern.length !== parts.length) continue;
    const params: Record<string, string> = {};
    const matched = pattern.every((segment, index) => {
      const actual = parts[index] as string;
      if (segment.startsWith(":")) {
        params[segment.slice(1)] = decodeURIComponent(actual);
        return true;
      }
      return segment === actual;
    });
    if (matched) return { route, params };
  }
  if (parts.length === 0) {
    const root = ROUTES.find((r) => r.path === "/");
    if (root) return { route: root, params: {} };
  }
  return undefined;
}
